/* eslint-disable jsx-a11y/control-has-associated-label */
import React, {
    useCallback, useRef, useState, useLayoutEffect,
    useEffect,
} from 'react';
import cn from 'classnames';
import isTomorrow from 'date-fns/is_tomorrow';

import { Mobile, Desktop } from 'app/components/Responsive';

import { getDateData } from 'app/utils/date';

import { filterHiddenDates } from 'app/const/hiddenPeriods';

import { ISODate, AvailableDate, Colors } from '../DatetimeSelect.types';

import arrow from './arrow.img.svg';
import arrowDisabled from './arrow.disabled.img.svg';
import css from './dateSelect.module.scss';

interface DateSelectProps {
    colors: Colors,
    disabled?: boolean,
    deliveryDates?: AvailableDate[],
    selectedDate: ISODate,
    responsive: boolean,
    onSelectDate: (nextDate: ISODate) => void, // eslint-disable-line no-unused-vars
}

export function DateSelect({
    colors,
    disabled = false,
    deliveryDates = [],
    selectedDate,
    responsive,
    onSelectDate,
}: DateSelectProps) {
    const scrollContainer = useRef<HTMLDivElement>(null);
    const scrollList = useRef<HTMLDivElement>(null);

    const [isScrolledToLeft, setIsScrolledToLeft] = useState(false);
    const [isScrolledToRight, setIsScrolledToRight] = useState(false);

    const visibleDates: AvailableDate[] = disabled
        ? deliveryDates
        : filterHiddenDates(deliveryDates, selectedDate);


    const resolveScrolledState = () => {
        if (!scrollContainer.current) return;
        if (!scrollList.current) return;

        const c = scrollContainer.current;
        const l = scrollList.current;

        const toLeft = c.scrollLeft < 1;
        const toRight = l.scrollWidth - (c.clientWidth + c.scrollLeft) < 1;

        setIsScrolledToLeft(toLeft);
        setIsScrolledToRight(toRight);
    };

    useLayoutEffect(() => {
        resolveScrolledState();
    }, []);

    useEffect(
        () => {
            onSelectDate(selectedDate);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );


    const handleScrollBack = useCallback(async () => {
        if (!scrollContainer.current) return;
        const scrollDistance = scrollContainer.current.clientWidth;
        scrollContainer.current.scrollBy(-1 * (scrollDistance + 8), 0);
        resolveScrolledState();
    }, []);

    const handleScrollForward = useCallback(async () => {
        if (!scrollContainer.current) return;
        const scrollDistance = scrollContainer.current.clientWidth;
        scrollContainer.current.scrollBy(scrollDistance + 8, 0);
        resolveScrolledState();
    }, []);


    const wrapperClasses = cn({
        [css.datesWrapper]: true,
        [css.responisveContainer]: responsive,
    });

    const scrollButtonsColorsClass = colors === 'white'
        ? css.dateScrollButtonWhite
        : css.dateScrollButtonTrasnparent;

    return (
        <div className={wrapperClasses}>
            {!disabled && (
                <Desktop>
                    <button
                        type="button"
                        className={cn([css.dateScrollButton, css.dateScrollButtonBack, scrollButtonsColorsClass])}
                        disabled={isScrolledToLeft}
                        onClick={handleScrollBack}
                    >
                        <img src={isScrolledToLeft ? arrowDisabled : arrow} alt="" />
                    </button>
                </Desktop>
            )}
            <div className={css.datesListContainer} ref={scrollContainer}>
                <div className={css.datesList} ref={scrollList}>
                    {visibleDates.map((aDate) => {
                        const { date } = aDate;

                        const dateData = getDateData(date);
                        const title = isTomorrow(new Date(date)) ? 'Завтра' : dateData.weekDayShortName;
                        const subtitle = `${dateData.date} ${dateData.monthShortNameForDate}`;

                        const onClick = () => onSelectDate(date);

                        const buttonClasses = cn({
                            [css.dateSelectButton]: true,
                            [css.dateSelectButtonActive]: date === selectedDate,
                        });

                        const titleClasses = cn({
                            [css.dateSelectTitle]: true,
                            [css.dateSelectTitleWihte]: colors === 'white',
                            [css.active]: date === selectedDate,
                        });

                        const subtitleClasses = cn({
                            [css.dateSelectSubtitle]: true,
                            [css.dateSelectSubtitleWhite]: colors === 'white',
                            [css.active]: date === selectedDate,
                        });

                        return (
                            <button
                                type="button"
                                onClick={onClick}
                                disabled={disabled}
                                key={date}
                                className={buttonClasses}
                            >
                                <p className={titleClasses}>
                                    {title}
                                </p>
                                <p className={subtitleClasses}>
                                    {subtitle}
                                </p>
                            </button>
                        );
                    })}
                    <Mobile>
                        <div className={css.datesListSpacer} />
                    </Mobile>
                </div>
            </div>
            {!disabled && (
                <Desktop>
                    <button
                        type="button"
                        className={cn([css.dateScrollButton, css.dateScrollButtonForward, scrollButtonsColorsClass])}
                        disabled={isScrolledToRight}
                        onClick={handleScrollForward}
                    >
                        <img src={isScrolledToRight ? arrowDisabled : arrow} alt="" />
                    </button>
                </Desktop>
            )}
        </div>
    );
}

/* DATE SELECT WITH SCROLL
export function DateSelect({
    colors,
    disabled = false,
    deliveryDates = [],
    selectedDate,
    responsive,
    onSelectDate,
}: DateSelectProps) {
    const scrollContainer = useRef<HTMLDivElement>(null);
    const scrollList = useRef<HTMLDivElement>(null);
    const dateButtonsRef = useRef<Map<ISODate, HTMLButtonElement>>(new Map());
    const prevDeliveryDatesLength = useRef<number>(deliveryDates.length);

    const [isScrolledToLeft, setIsScrolledToLeft] = useState(false);
    const [isScrolledToRight, setIsScrolledToRight] = useState(false);

    const scrollToSelectedDate = useCallback((date: ISODate) => {
        const buttonElement = dateButtonsRef.current.get(date);
        if (buttonElement && scrollContainer.current) {
            buttonElement.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'start',
            });
        }
    }, []);

    // Прокручиваем к выбранной дате при изменении selectedDate
    useLayoutEffect(() => {
        if (selectedDate) {
            scrollToSelectedDate(selectedDate);
        }
    }, [selectedDate, scrollToSelectedDate]);

    // Прокручиваем к выбранной дате при изменении списка дат
    useLayoutEffect(() => {
        const isListChanged = prevDeliveryDatesLength.current !== deliveryDates.length;

        if (isListChanged && selectedDate) {
            // Небольшая задержка чтобы дать время DOM обновиться
            const timer = setTimeout(() => {
                scrollToSelectedDate(selectedDate);
            }, 50);

            // Обновляем предыдущую длину
            prevDeliveryDatesLength.current = deliveryDates.length;

            return () => clearTimeout(timer);
        }

        // Всегда обновляем предыдущую длину
        prevDeliveryDatesLength.current = deliveryDates.length;
    }, [deliveryDates.length, selectedDate, scrollToSelectedDate]);

    const resolveScrolledState = () => {
        if (!scrollContainer.current) return;
        if (!scrollList.current) return;

        const c = scrollContainer.current;
        const l = scrollList.current;

        const toLeft = c.scrollLeft < 1;
        const toRight = l.scrollWidth - (c.clientWidth + c.scrollLeft) < 1;

        setIsScrolledToLeft(toLeft);
        setIsScrolledToRight(toRight);
    };

    useLayoutEffect(() => {
        resolveScrolledState();
    }, [deliveryDates]);

    const handleScrollBack = useCallback(async () => {
        if (!scrollContainer.current) return;
        const scrollDistance = scrollContainer.current.clientWidth;
        scrollContainer.current.scrollBy(-1 * (scrollDistance + 8), 0);
        resolveScrolledState();
    }, []);

    const handleScrollForward = useCallback(async () => {
        if (!scrollContainer.current) return;
        const scrollDistance = scrollContainer.current.clientWidth;
        scrollContainer.current.scrollBy(scrollDistance + 8, 0);
        resolveScrolledState();
    }, []);

    const setDateButtonRef = useCallback((date: ISODate, element: HTMLButtonElement | null) => {
        if (element) {
            dateButtonsRef.current.set(date, element);
        } else {
            dateButtonsRef.current.delete(date);
        }
    }, []);

    const wrapperClasses = cn({
        [css.datesWrapper]: true,
        [css.responisveContainer]: responsive,
    });

    const scrollButtonsColorsClass = colors === 'white'
        ? css.dateScrollButtonWhite
        : css.dateScrollButtonTrasnparent;

    return (
        <div className={wrapperClasses}>
            {!disabled && (
                <Desktop>
                    <button
                        type="button"
                        className={cn([css.dateScrollButton, css.dateScrollButtonBack, scrollButtonsColorsClass])}
                        disabled={isScrolledToLeft}
                        onClick={handleScrollBack}
                    >
                        <img src={isScrolledToLeft ? arrowDisabled : arrow} alt="" />
                    </button>
                </Desktop>
            )}
            <div
                className={css.datesListContainer}
                ref={scrollContainer}
                onScroll={resolveScrolledState}
            >
                <div className={css.datesList} ref={scrollList}>
                    {deliveryDates.map((aDate) => {
                        const { date } = aDate;

                        const dateData = getDateData(date);
                        const title = isTomorrow(new Date(date)) ? 'Завтра' : dateData.weekDayShortName;
                        const subtitle = `${dateData.date} ${dateData.monthShortNameForDate}`;

                        const onClick = () => {
                            onSelectDate(date);
                            scrollToSelectedDate(date);
                        };

                        const buttonClasses = cn({
                            [css.dateSelectButton]: true,
                            [css.dateSelectButtonActive]: date === selectedDate,
                        });

                        const titleClasses = cn({
                            [css.dateSelectTitle]: true,
                            [css.dateSelectTitleWihte]: colors === 'white',
                            [css.active]: date === selectedDate,
                        });

                        const subtitleClasses = cn({
                            [css.dateSelectSubtitle]: true,
                            [css.dateSelectSubtitleWhite]: colors === 'white',
                            [css.active]: date === selectedDate,
                        });

                        return (
                            <button
                                type="button"
                                onClick={onClick}
                                disabled={disabled}
                                key={date}
                                className={buttonClasses}
                                ref={(el) => setDateButtonRef(date, el)}
                            >
                                <p className={titleClasses}>
                                    {title}
                                </p>
                                <p className={subtitleClasses}>
                                    {subtitle}
                                </p>
                            </button>
                        );
                    })}
                    <Mobile>
                        <div className={css.datesListSpacer} />
                    </Mobile>
                </div>
            </div>
            {!disabled && (
                <Desktop>
                    <button
                        type="button"
                        className={cn([css.dateScrollButton, css.dateScrollButtonForward, scrollButtonsColorsClass])}
                        disabled={isScrolledToRight}
                        onClick={handleScrollForward}
                    >
                        <img src={isScrolledToRight ? arrowDisabled : arrow} alt="" />
                    </button>
                </Desktop>
            )}
        </div>
    );
}

*/
